import React, { useState, useEffect } from "react";
import { Copy, Heart } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import SocialShareDropdown from "./SocialShareDropdown";
import { favoriteService } from "@/services/favoriteService";

interface FeedbackActionButtonsProps {
  content: string;
  targetId?: string;
  targetType?: 'location' | 'image' | 'prediction';
}

const FeedbackActionButtons: React.FC<FeedbackActionButtonsProps> = ({
  content,
  targetId,
  targetType,
}) => {
  const { toast } = useToast();
  const [isFavorite, setIsFavorite] = useState(false);
  const [isTogglingFavorite, setIsTogglingFavorite] = useState(false);

  // Load initial favorite status
  useEffect(() => {
    if (targetId && targetType) {
      favoriteService.getFavoriteStatus(targetId, targetType).then(setIsFavorite);
    }
  }, [targetId, targetType]);

  const handleToggleFavorite = async () => {
    if (!targetId || !targetType || isTogglingFavorite) return;
    
    setIsTogglingFavorite(true);
    const result = await favoriteService.toggleFavorite(targetId, targetType, isFavorite);
    setIsTogglingFavorite(false);

    if (result.success) {
      setIsFavorite(result.isFavorite);
      toast({
        title: result.isFavorite ? "Added to favorites" : "Removed from favorites",
        description: result.isFavorite 
          ? "This target has been added to your favorites." 
          : "This target has been removed from your favorites.",
      });
    } else {
      toast({
        title: "Failed to update favorite",
        description: result.error || "Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      toast({
        title: "Copied to clipboard",
        description: "The feedback analysis has been copied to your clipboard.",
      });
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      toast({
        title: "Copy failed",
        description: "Failed to copy to clipboard. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <TooltipProvider>
      <div className="flex gap-2 mt-4 justify-end">
        {targetId && targetType && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className={`w-8 h-8 rounded-full flex justify-center items-center transition-colors ${
                  isTogglingFavorite ? 'opacity-50 cursor-wait' : 'hover:bg-accent'
                }`}
                onClick={handleToggleFavorite}
                disabled={isTogglingFavorite}
                aria-label={isFavorite ? "Remove from favorites" : "Add to favorites"}
              >
                <Heart 
                  className={`h-4 w-4 transition-colors ${
                    isFavorite 
                      ? 'fill-primary text-primary' 
                      : 'text-muted-foreground'
                  }`}
                />
              </button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{isFavorite ? "Remove from favorites" : "Add to favorites"}</p>
            </TooltipContent>
          </Tooltip>
        )}
        
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="w-8 h-8 rounded-full flex justify-center items-center hover:bg-accent transition-colors"
              onClick={handleCopy}
              aria-label="Copy feedback analysis"
            >
              <Copy className="h-4 w-4 text-muted-foreground" />
            </button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Copy feedback to clipboard</p>
          </TooltipContent>
        </Tooltip>
        
        <SocialShareDropdown content={content} />
      </div>
    </TooltipProvider>
  );
};

export default FeedbackActionButtons;